import { recipeWriteVersion } from '../../../../../services/recipeWriteConflict';
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import { useSubrecipeReviewStore } from "../../../../../stores/appSubrecipeReviewStore";
import { ReviewIngredient } from "../../../../../types/reviewTypes";
import {
    fetchSubrecipeById,
    updateSubrecipe,
    SubrecipeIngredientInput,
    Subrecipe,
} from "../../../../../services/subrecipes/subrecipesService";
import { toIngredientInput } from "../../new/CostPrice/useSubrecipeDetail";
import { matchesSubrecipeRow, isSubrecipeRowShared, templateQuantityInUnit } from "../reviewUtils";
import {
    alreadyOnCandidate,
    candidateUnit,
    MatchingIngredientRef,
    ReviewWriteAdapter,
    sameTemplateName,
} from "../reviewAdapter";

/**
 * The subrecipe review's write side: every edit is a read-modify-write of the
 * subrecipe through the regular update endpoint, exactly as the subrecipe editor
 * does it. The review flow has no privileged write path of its own.
 *
 * Nesting is deliberately absent — the template import never produces a nested
 * reference, and `allowedSwapKinds` keeps the review from introducing one.
 */

/** The report ingredients of one subrecipe, for the shared-row check. */
function reportIngredientsOf(subrecipeId: string): ReviewIngredient[] | undefined {
    return useSubrecipeReviewStore.getState().reviewReport?.subrecipes
        .find(item => item.subrecipeId === subrecipeId)?.ingredients;
}

/** Rows to keep when dropping the one this report ingredient created. */
async function rowsWithout(subrecipeId: string, ingredient: ReviewIngredient, ingredientIndex: number) {
    const subrecipe = await fetchSubrecipeById(subrecipeId);
    const reportIngredients = reportIngredientsOf(subrecipeId);
    const remaining = subrecipe.ingredients
        .filter(row => !matchesSubrecipeRow(row, ingredient)
            || isSubrecipeRowShared(row, reportIngredients, ingredientIndex))
        .map(toIngredientInput);
    return { subrecipe, remaining };
}

async function saveIngredients(subrecipe: Subrecipe, ingredients: SubrecipeIngredientInput[]): Promise<void> {
    await updateSubrecipe({ id: subrecipe.id, expectedUpdatedAt: recipeWriteVersion(subrecipe), ingredients });
    // The card prices itself from the subrecipes store, so it has to see the write.
    await useSubrecipesStore.getState().reload();
}

export const subrecipeReviewAdapter: ReviewWriteAdapter = {
    // No 'subrecipe': the import flow is nesting-free by design, and a swap must
    // not be the back door that introduces nesting (and its cycle/depth risks).
    allowedSwapKinds: ['product', 'custom'],

    async remove(subrecipeId, ingredient, ingredientIndex) {
        const { subrecipe, remaining } = await rowsWithout(subrecipeId, ingredient, ingredientIndex);
        if (remaining.length !== subrecipe.ingredients.length) {
            await saveIngredients(subrecipe, remaining);
        }
        useSubrecipeReviewStore.getState().updateIngredient(subrecipeId, ingredientIndex, { reviewAction: 'removed' });
    },

    /**
     * Change only the quantity of the already-matched product — no remove + re-add
     * round trip. A row shared with other report ingredients is edited once and
     * every entry pointing at it is patched so the UI stays truthful.
     */
    async setQuantity(subrecipeId, ingredient, _ingredientIndex, quantity) {
        const subrecipe = await fetchSubrecipeById(subrecipeId);
        const targetRow = subrecipe.ingredients.find(row => matchesSubrecipeRow(row, ingredient));
        if (!targetRow) throw new Error('Ingredient row not found in subrecipe');

        const ingredients = subrecipe.ingredients
            .map(row => (row === targetRow ? { ...row, quantityNeeded: quantity } : row))
            .map(toIngredientInput);
        await saveIngredients(subrecipe, ingredients);

        reportIngredientsOf(subrecipeId)?.forEach((other, i) => {
            if (other.reviewAction !== 'removed' && matchesSubrecipeRow(targetRow, other)) {
                useSubrecipeReviewStore.getState().updateIngredient(subrecipeId, i, { quantityNeeded: quantity });
            }
        });
    },

    /**
     * Point one report ingredient at `candidate`: drop the row the import created
     * for it (unless another still-active report ingredient of the same subrecipe
     * points at that same row), add the new row, then patch the review report.
     */
    async swap(subrecipeId, ingredient, ingredientIndex, candidate, quantity) {
        if (candidate.kind === 'subrecipe') {
            throw new Error('Nesting a subrecipe is not available in the import review');
        }
        const { subrecipe, remaining } = await rowsWithout(subrecipeId, ingredient, ingredientIndex);

        const newRow: SubrecipeIngredientInput = {
            articleId: candidate.articleId,
            productName: candidate.displayName,
            quantityNeeded: quantity,
            quantityInfo: candidate.quantityInfo,
            // Custom rows are resolved by (articleId, platform) at cost time, so the
            // custom's own platform + free-text supplier name must be stored here.
            platform: candidate.platform,
            customPlatformName: candidate.customPlatformName,
            isCustom: candidate.kind === 'custom',
        };

        await saveIngredients(subrecipe, [...remaining, newRow]);

        useSubrecipeReviewStore.getState().updateIngredient(subrecipeId, ingredientIndex, {
            reviewAction: 'swapped',
            status: 'matched',
            productName: candidate.displayName,
            articleId: candidate.articleId,
            platform: candidate.platform,
            // Keeps the row honest after the swap: custom rows are priced from the
            // stored custom price and have no order history to show.
            isCustom: candidate.kind === 'custom',
            customPlatformName: candidate.customPlatformName,
            quantityNeeded: quantity,
            quantityInfo: candidate.quantityInfo,
        });
    },

    /**
     * Every OTHER subrecipe in the report carrying the same template ingredient
     * name, so one correction can be pushed to all of them. Rows already removed,
     * or already pointing at the candidate, are skipped.
     */
    findSameIngredientElsewhere(templateName, sourceSubrecipeId, candidate, appliedQuantity) {
        const report = useSubrecipeReviewStore.getState().reviewReport;
        if (!report) return [];

        const targetUnit = candidateUnit(candidate);
        const matches: MatchingIngredientRef[] = [];

        report.subrecipes.forEach(item => {
            if (item.subrecipeId === sourceSubrecipeId) return;
            item.ingredients.forEach((ingredient, index) => {
                if (!sameTemplateName(ingredient.templateName, templateName)) return;
                if (ingredient.reviewAction === 'removed') return;
                if (alreadyOnCandidate(ingredient, candidate)) return;

                const converted = templateQuantityInUnit(ingredient.templateQuantity, ingredient.templateUnit, targetUnit);
                matches.push({
                    itemId: item.subrecipeId,
                    itemName: item.name,
                    ingredientIndex: index,
                    ingredient,
                    currentMatchName: ingredient.productName,
                    quantity: converted ?? appliedQuantity,
                });
            });
        });

        return matches;
    },
};
